import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/PageLayout";
import { ApiError, api } from "../lib/api";
import { formatJstDateTime } from "../lib/format";

const SECTION_LABEL: Record<string, string> = {
  counter: "カウンター",
  zashiki: "掘りごたつ",
  any: "席指定なし",
};

const STATUS_LABEL: Record<string, string> = {
  pending: "確認中",
  confirmed: "確定",
  cancelled: "キャンセル済み",
  completed: "ご来店済み",
  no_show: "キャンセル扱い",
};

export function MyReservationsPage() {
  const queryClient = useQueryClient();
  const listQuery = useQuery({ queryKey: ["myReservations"], queryFn: api.myReservations });
  const [pendingCancelId, setPendingCancelId] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.cancelReservation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myReservations"] });
      setPendingCancelId(null);
    },
    onError: (e) => {
      if (e instanceof ApiError && (e.detail as { error?: string })?.error === "FORBIDDEN") {
        setCancelError("このご予約はキャンセルできません。");
      } else {
        setCancelError("キャンセルに失敗しました。時間をおいて再度お試しください。");
      }
    },
  });

  return (
    <PageLayout title="ご予約の確認">
      <div className="flex flex-col gap-3 pb-24">
        {listQuery.isLoading ? (
          <p className="text-sm text-neutral-500">読み込み中…</p>
        ) : listQuery.error ? (
          <p className="text-sm text-red-600">予約情報を取得できませんでした。</p>
        ) : !listQuery.data || listQuery.data.reservations.length === 0 ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-neutral-500">現在、ご予約はありません。</p>
            <Link
              to="/reservation/new"
              className="rounded-lg bg-neutral-900 px-4 py-3 text-center text-sm font-semibold text-white"
            >
              新しく予約する
            </Link>
          </div>
        ) : (
          listQuery.data.reservations.map((r) => {
            const cancellable = r.status === "pending" || r.status === "confirmed";
            const isThisPending = pendingCancelId === r.id;
            return (
              <div key={r.id} className="rounded-md border border-neutral-200 p-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-semibold">{formatJstDateTime(r.reservedAt)}</span>
                  <span className="ml-3 rounded bg-neutral-100 px-2 py-0.5 text-xs">
                    {STATUS_LABEL[r.status] ?? r.status}
                  </span>
                </div>
                <p className="mt-1 text-sm text-neutral-700">
                  {r.partySize}名 ／ {SECTION_LABEL[r.seatSection] ?? r.seatSection}
                </p>
                {r.notes ? (
                  <p className="mt-1 text-xs text-neutral-500">ご要望: {r.notes}</p>
                ) : null}
                {cancellable ? (
                  isThisPending ? (
                    <div className="mt-3 flex flex-col gap-2">
                      <p className="text-sm">本当にキャンセルしますか？</p>
                      {cancelError ? <p className="text-xs text-red-600">{cancelError}</p> : null}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => cancelMutation.mutate(r.id)}
                          disabled={cancelMutation.isPending}
                          className="flex-1 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white disabled:bg-red-300"
                        >
                          {cancelMutation.isPending ? "キャンセル中…" : "キャンセル確定"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setPendingCancelId(null);
                            setCancelError(null);
                          }}
                          disabled={cancelMutation.isPending}
                          className="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm font-semibold disabled:opacity-50"
                        >
                          戻る
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setPendingCancelId(r.id);
                        setCancelError(null);
                      }}
                      className="mt-3 w-full rounded-md border border-red-300 px-3 py-2 text-sm font-semibold text-red-600"
                    >
                      キャンセルする
                    </button>
                  )
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </PageLayout>
  );
}
